import createFetchClient, { type Middleware } from 'openapi-fetch';
import createClient from 'openapi-react-query';
import { Problem, toProblem, UNKNOWN_PROBLEM } from './problem';
import type { paths } from './schema';

// Every failure leaves a client the same way: rejected with a `Problem`, so
// callers branch on `code` and never read a status or a message.
const problemMiddleware: Middleware = {
  async onResponse({ response }) {
    if (!response.ok) {
      throw await toProblem(response);
    }
    // `openapi-fetch` parses a 2xx body itself, after every middleware has run
    // and with no hook for a failure, so a proxy's HTML page under a 200 would
    // reject with a raw SyntaxError. Read a copy here, while the response is
    // still ours to describe. An empty body passes: it is a call with no data.
    try {
      const body = await response.clone().text();
      if (body) {
        JSON.parse(body);
      }
    } catch {
      throw new Problem(
        UNKNOWN_PROBLEM,
        response.status,
        'The API answered with something that is not JSON.',
      );
    }
    return undefined;
  },
  async onError({ error }) {
    return await toProblem(error);
  },
};

/**
 * A coach API client for one application (ADR 0019): the `openapi-react-query`
 * adapter, plus `client`, the `openapi-fetch` instance under it, for a
 * mutation that maps application inputs itself. Every call creates its own.
 */
export function createCoachApi({
  baseUrl,
  getAccessToken,
}: {
  /** Base URL of the coach surface, e.g. `http://localhost:8080/coach/v1`. */
  baseUrl: string | undefined;
  /** Asked before every request; no token sends no Authorization header. */
  getAccessToken: () => string | undefined | Promise<string | undefined>;
}) {
  if (!baseUrl) {
    // Without it `openapi-fetch` falls back to the page's own origin and every
    // call comes back as the index page; fail where the cause is still visible.
    throw new Error(
      'createCoachApi: baseUrl is required. The application configures it from its environment.',
    );
  }

  // The generated types allow a list parameter (e.g. `state`); Huma reads it
  // comma-separated and takes only the first value under the default explode.
  const client = createFetchClient<paths>({
    baseUrl,
    querySerializer: { array: { style: 'form', explode: false } },
  });
  client.use({
    async onRequest({ request }) {
      // `onError` sees only what `fetch` rejects with, not what is thrown here.
      let token: string | undefined;
      try {
        token = await getAccessToken();
      } catch (error) {
        throw await toProblem(error);
      }
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }
      return request;
    },
  });
  client.use(problemMiddleware);

  return { ...createClient(client), client };
}
